import React, { useState, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import URI from 'urijs'

import {
  Grid, Typography, Toolbar, IconButton,
  Button, Checkbox, Tooltip, Link,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, TablePagination,
  Card, CardContent, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem
} from '@material-ui/core'

import { makeStyles, fade, darken, withStyles } from '@material-ui/core/styles'

import { Close as CloseIcon } from '@material-ui/icons'

import { getDestinationRequests, updateDestinationRequests } from '../../../../../redux/actions'

import { NotificationToast, ModalToast } from '../../../tools'

import { truncateText, parseUnitInfo } from '../../../../../utils'

const learnsafeGreen = '#A4D164'
const learnsafeRed = '#e57373'
const learnsafeBlue = '#5291fc'

const headCells = [
  { id: 'name', label: 'Requested By' },
  { id: 'email', label: 'Email' },
  { id: 'domain', label: 'Domain' },
  { id: 'comment', label: 'Reason' },
  { id: 'destinationList', label: 'List' },
  { id: 'status', label: 'Status' }
]

const useStyles = makeStyles((theme) => ({
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1
  },
  rowSelected: {
    '&.Mui-selected': {
      backgroundColor: fade(theme.palette.warning.light, 0.25),
      '&:hover': {
        backgroundColor: fade(theme.palette.warning.light, 0.35)
      }
    }
  },
  approveSelected: {
    '&.Mui-selected': {
      backgroundColor: fade(learnsafeGreen, 0.25),
      '&:hover': {
        backgroundColor: fade(learnsafeGreen, 0.35)
      }
    }
  },
  denySelected: {
    '&.Mui-selected': {
      backgroundColor: fade(learnsafeRed, 0.25),
      '&:hover': {
        backgroundColor: fade(learnsafeRed, 0.35)
      }
    }
  },
  defaultCheckbox: {
    color: theme.palette.warning.light,
    '&:hover': {
      backgroundColor: fade(theme.palette.warning.light, 0.25)
    },
    '&.Mui-checked': {
      color: theme.palette.warning.light,
      '&:hover': {
        backgroundColor: fade(theme.palette.warning.light, 0.25)
      }
    }
  },
  approvedCheckbox: {
    color: learnsafeGreen,
    '&:hover': {
      backgroundColor: fade(learnsafeGreen, 0.25)
    },
    '&.Mui-checked': {
      color: learnsafeGreen,
      '&:hover': {
        backgroundColor: fade(learnsafeGreen, 0.25)
      }
    }
  },
  deniedCheckbox: {
    color: learnsafeRed,
    '&:hover': {
      backgroundColor: fade(learnsafeRed, 0.25)
    },
    '&.Mui-checked': {
      color: learnsafeRed,
      '&:hover': {
        backgroundColor: fade(learnsafeRed, 0.25)
      }
    }
  },
  approvedText: {
    color: learnsafeGreen,
    fontWeight: 600
  },
  deniedText: {
    color: learnsafeRed,
    fontWeight: 600
  },
  pendingText: {
    color: theme.palette.warning.light,
    fontWeight: 600
  },
  saveBtn: {
    backgroundColor: learnsafeBlue,
    color: '#FFF',
    '&:hover': {
      backgroundColor: darken(learnsafeBlue, 0.2)
    }
  },
  requestsContainer: {
    maxHeight: '65vh',
    overflowY: 'scroll'
  },
  disabledSelected: {
    '&.Mui-selected': {
      backgroundColor: 'rgba(0, 0, 0, 0.10)',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.10)'
      }
    }
  },
  disabledText: {
    color: theme.palette.action.disabled,
    fontWeight: 600
  },
  fixSpacing: {
    width: '100%',
    margin: 'auto'
  },
  greenBtn: {
    backgroundColor: learnsafeGreen,
    color: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: darken(learnsafeGreen, 0.15)
    }
  }
}))

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1)
  },
  highlight: {
    color: theme.palette.text.primary,
    backgroundColor: fade(theme.palette.warning.light, 0.25)
  },
  title: {
    flex: '1 1 100%'
  },
  approveBtn: {
    backgroundColor: learnsafeGreen,
    color: '#fff',
    '&:hover': {
      backgroundColor: darken(learnsafeGreen, 0.2)
    }
  },
  denyBtn: {
    backgroundColor: learnsafeRed,
    color: '#fff',
    '&:hover': {
      backgroundColor: darken(learnsafeRed, 0.2)
    }
  },
  approvedText: {
    color: learnsafeGreen,
    fontWeight: 600
  },
  deniedText: {
    color: learnsafeRed,
    fontWeight: 600
  }
}))

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    color: theme.palette.background.paper,
    backgroundColor: theme.palette.text.secondary,
    boxShadow: theme.shadows[3],
    leaveTouchDelay: 10000
  },
  arrow: {
    color: theme.palette.action.active
  }
}))(Tooltip)

const StyledSwitch = withStyles({
  switchBase: {
    color: 'white',
    '&$checked': {
      color: '#A4D164'
    },
    '&$checked + $track': {
      backgroundColor: '#A4D164'
    }
  },
  checked: {},
  track: {}
})(Switch)

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles()
  const {
    selectedCount, approvedCount, deniedCount,
    onApprove = () => { }, onDeny = () => { }
  } = props

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: selectedCount > 0
      })}
    >
      {
        selectedCount > 0 ? (
          <Grid container direction='row' justify='space-between'>
            <Grid item>
              <Typography className={classes.title} color='inherit' variant='subtitle1' component='div'>
                {selectedCount} selected
              </Typography>
            </Grid>

            <Grid item>
              <Grid container direction='row' spacing={2} className='mx-auto w-100'>
                <Grid item>
                  <Button
                    variant='contained'
                    className={classes.approveBtn}
                    onClick={onApprove}
                  >
                    Approve
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    variant='contained'
                    className={classes.denyBtn}
                    onClick={onDeny}
                  >
                    Deny
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid container direction='row' justify='flex-start' spacing={2} className='mx-auto w-100' alignItems='center'>
            <Grid item className='pl-0 py-0'>
              <Typography className={classes.title} variant='h6' id='tableTitle' component='div'>
                Block Requests:
              </Typography>
            </Grid>

            {
              Boolean(approvedCount) || Boolean(deniedCount) ? (
                <>
                  <Grid item className='py-0'>
                    <Grid container direction='row'>
                      <Grid item>
                        <Typography variant='button' className={classes.approvedText}>Approved: </Typography>
                      </Grid>

                      <Grid item>
                        <Typography variant='button'>{approvedCount}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item className='py-0'>
                    <Grid container direction='row'>
                      <Grid item>
                        <Typography variant='button' className={classes.deniedText}>Denied: </Typography>
                      </Grid>

                      <Grid item>
                        <Typography variant='button'>{deniedCount}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              ) : (<></>)
            }
          </Grid>
        )
      }
    </Toolbar>
  )
}

const EnhancedCiscoHead = (props) => {
  const {
    classes, onSelectAllClick, order, orderBy, selectedCount, onRequestSort, displayedCount, approvedCount, deniedCount
  } = props
  const createSortHandler = (property) => (event) => { onRequestSort(event, property) }

  const totalChecks = displayedCount - approvedCount - deniedCount

  return (
    <TableHead>
      <TableRow>
        <TableCell padding='checkbox'>
          <Checkbox
            indeterminate={(selectedCount > 0 && selectedCount < totalChecks) || (totalChecks === 0 && (approvedCount > 0 || deniedCount > 0))}
            checked={displayedCount > 0 && selectedCount === totalChecks && totalChecks > 0}
            onChange={onSelectAllClick}
            className={clsx(classes.defaultCheckbox, {
              [classes.approvedCheckbox]: approvedCount === displayedCount,
              [classes.deniedCheckbox]: deniedCount === displayedCount
            })}
            inputProps={{ 'aria-label': 'select all desserts' }}
          />
        </TableCell>

        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align='center'
            padding='default'
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const CiscoRequests = (props) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const { requests: ciscoRequests } = useSelector(state => state.integrations)
  const TreeData = useSelector(state => state.auth.unitTree)

  const [unitDisplays, setUnitDisplays] = useState([])
  const [destinationRequests, setDestinationRequests] = useState([])
  const [selected, setSelected] = useState([])
  const [approvedSelected, setApprovedSelected] = useState([])
  const [deniedSelected, setDeniedSelected] = useState([])
  const [displayedRows, setDisplayedRows] = useState([])
  const [approvedDomains, setApprovedDomains] = useState([])
  const [subdomains, setSubdomains] = useState([])

  const [confirmShow, setConfirmShow] = useState(false)

  const [approvedDisplayed, setApprovedDisplayed] = useState(0)
  const [deniedDisplayed, setDeniedDisplayed] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState(headCells[0].id)

  useMemo(() => { if (TreeData) { setUnitDisplays(parseUnitInfo(TreeData)) } }, [TreeData])

  useEffect(() => {
    const fireFailure = () => NotificationToast(true, 'Failed to get destination requests.')
    dispatch(getDestinationRequests(() => { }, fireFailure))
  }, [dispatch])

  useMemo(() => {
    if (ciscoRequests && Array.isArray(ciscoRequests)) { setDestinationRequests(ciscoRequests) }
  }, [ciscoRequests])

  useMemo(() => {
    const descendingComparator = (a, b, orderBy) => {
      if (b[orderBy] < a[orderBy]) {
        return -1
      }
      if (b[orderBy] > a[orderBy]) {
        return 1
      }
      return 0
    }

    const getComparator = (order, orderBy) => {
      return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy)
    }

    const stableSort = (requestsArr, comparator) => {
      const stabilizedThis = requestsArr.reduce((arr, requestInfo, index) => {
        if (requestInfo && requestInfo.destinationID) {
          const isApproved = approvedSelected.indexOf(requestInfo.destinationID) !== -1
          const isDenied = deniedSelected.indexOf(requestInfo.destinationID) !== -1

          const unitDisplay = unitDisplays.find((displayInfo) => displayInfo && displayInfo.unitID === requestInfo.unitID)

          requestInfo = {
            ...requestInfo,
            status: isApproved ? 'APPROVED' : isDenied ? 'DENIED' : 'PENDING',
            unitDisplay: unitDisplay && unitDisplay.display ? unitDisplay.display : ''
          }
          arr.push([requestInfo, index])
        }

        return arr
      }, [])

      stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0])
        if (order !== 0) return order
        return a[1] - b[1]
      })

      return stabilizedThis.map((el) => el[0])
    }

    if (destinationRequests && order && orderBy && page != null && rowsPerPage != null && approvedSelected && deniedSelected && unitDisplays) {
      const destinationsDisplayed = stableSort(destinationRequests, getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

      const counts = destinationsDisplayed.reduce((obj, destinationInfo) => {
        if (destinationInfo && destinationInfo.status) {
          if (destinationInfo.status === 'APPROVED') { obj.approved += 1 }
          if (destinationInfo.status === 'DENIED') { obj.denied += 1 }
        }

        return obj
      }, { approved: 0, denied: 0 })

      setApprovedDisplayed(counts.approved)
      setDeniedDisplayed(counts.denied)
      setDisplayedRows(destinationsDisplayed)
    }
  }, [order, orderBy, page, rowsPerPage, destinationRequests, approvedSelected, deniedSelected, unitDisplays])

  const handleSelectAllClick = (event) => {
    const totalChecks = displayedCount - approvedDisplayed - deniedDisplayed
    const isIndeterminate = (totalChecks === 0 && (approvedDisplayed > 0 || deniedDisplayed > 0))

    if (event.target.checked) {
      if (isIndeterminate) {
        const displayed = displayedRows.reduce((obj, requestInfo) => {
          if (requestInfo && requestInfo.status) {
            if (requestInfo.status === 'APPROVED') { obj.approved.push(requestInfo.destinationID) }
            if (requestInfo.status === 'DENIED') { obj.denied.push(requestInfo.destinationID) }
          }

          return obj
        }, { denied: [], approved: [] })

        const tempApproved = [...approvedSelected]
        const tempDenied = [...deniedSelected]

        setApprovedSelected(tempApproved.filter((approved) => displayed.approved.indexOf(approved) === -1))
        setDeniedSelected(tempDenied.filter((denied) => displayed.denied.indexOf(denied) === -1))
      } else {
        const newSelecteds = displayedRows.reduce((arr, requestInfo) => {
          if (requestInfo && requestInfo.destinationID) {
            const isApproved = approvedSelected.indexOf(requestInfo.destinationID) !== -1
            const isDenied = deniedSelected.indexOf(requestInfo.destinationID) !== -1

            if (!isApproved && !isDenied) { arr.push(requestInfo.destinationID) }
          }

          return arr
        }, [])

        setSelected(newSelecteds)
      }
    } else {
      if (displayedCount === deniedCount) {
        setDeniedSelected([])
      } else if (displayedCount === approvedCount) {
        setApprovedSelected([])
      } else {
        setSelected([])
      }
    }
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleRowClick = (isDisabled, destinationID, isApproved, isDenied) => {
    if (!isDisabled) {
      const tempSelected = [...selected]
      const tempApproved = [...approvedSelected]
      const tempDenied = [...deniedSelected]

      const isSelected = tempSelected.indexOf(destinationID) !== -1

      if (isApproved) {
        const newApproved = tempApproved.filter((destination) => destination !== destinationID)
        setApprovedSelected(newApproved)
      } else if (isDenied) {
        const newDenied = tempDenied.filter((destination) => destination !== destinationID)
        setDeniedSelected(newDenied)
      } else {
        if (isSelected) {
          const newSelected = tempSelected.filter((destination) => destination !== destinationID)
          setSelected(newSelected)
        } else {
          setSelected([...tempSelected, destinationID])
        }
      }
    }
  }

  const handleApprove = () => {
    setApprovedSelected([...approvedSelected, ...selected])
    setSelected([])
  }

  const handleDeny = () => {
    setDeniedSelected([...deniedSelected, ...selected])
    setSelected([])
  }

  const handleSave = () => {
    if (approvedCount > 0 || deniedCount > 0) {
      let hasSubdomains = false

      const markedForApproved = approvedSelected.reduce((arr, destinationID) => {
        const requestApproved = destinationRequests.find((requestInfo) => requestInfo && requestInfo.destinationID && requestInfo.destinationID === destinationID)

        if (requestApproved && requestApproved.destinationID) {
          let requestUrl = ''

          if (!requestApproved.domain.startsWith('http')) {
            requestUrl = 'https://' + requestApproved.domain
          } else {
            requestUrl = requestApproved.domain
          }

          let subdomain = ''
          let domain = ''

          try {
            const requestURI = new URI(requestUrl)
            subdomain = requestURI.subdomain()
            domain = requestURI.domain()
          } catch (err) {
            // console.error(err)
          }

          if (subdomain !== '' && !hasSubdomains) { hasSubdomains = true }

          arr.push({
            destinationID: requestApproved.destinationID,
            destination: requestApproved.domain,
            comment: requestApproved.comment,
            unitID: requestApproved.unitID,
            hasSubdomain: subdomain !== '',
            domain
          })
        }
        return arr
      }, [])

      setApprovedDomains(markedForApproved)

      if (hasSubdomains) {
        setConfirmShow(true)
      } else {
        handleConfirm()
      }
    } else {
      NotificationToast(false, 'No requests have been selected.', true)
    }
  }

  const handleConfirm = () => {
    const deniedRequests = deniedSelected.map((destinationID) => ({ destinationID }))
    const approvedRequests = approvedDomains.reduce((arr, domainInfo) => {
      if (domainInfo && domainInfo.destinationID) {
        const isChecked = Boolean(subdomains.indexOf(domainInfo.destinationID) !== -1)

        arr.push({
          destinationID: domainInfo.destinationID,
          destination: isChecked ? domainInfo.domain : domainInfo.destination,
          comment: domainInfo.comment,
          unitID: domainInfo.unitID
        })
      }

      return arr
    }, [])

    if (Boolean(deniedRequests.length) || Boolean(approvedRequests.length)) {
      dispatch(updateDestinationRequests(approvedRequests, deniedRequests, fireRequestSuccess, fireRequestFailure))
    }
  }

  const handleConfirmClose = () => {
    setConfirmShow(false); setApprovedDomains([]); setSubdomains([])
  }

  const handleDomainChange = (e, approvedDestinationID) => {
    const tempSubdomains = [...subdomains]

    if (e.target.checked) {
      tempSubdomains.push(approvedDestinationID)
      setSubdomains(tempSubdomains)
    } else {
      const newDomains = tempSubdomains.filter((subdomain) => subdomain !== approvedDestinationID)
      setSubdomains(newDomains)
    }
  }

  const fireRequestSuccess = (destinations) => {
    if (destinations && destinations.rejected && destinations.accepted) {
      const tempRequests = destinationRequests.filter((requestInfo) => {
        return requestInfo && requestInfo.destinationID && Boolean(
          destinations.accepted.findIndex((destinationInfo) => destinationInfo && destinationInfo.destinationID !== requestInfo.destinationID) !== -1
        )
      })

      setDestinationRequests(tempRequests); handleConfirmClose()

      ModalToast('warning', 'Incomplete Requests', `The following ${destinations.rejected.length} requests have failed. Please check the requested destinations have a destination list configured.`)
    } else {
      const tempRequests = destinationRequests.filter((requestInfo) => {
        return requestInfo && requestInfo.destinationID &&
          approvedSelected.indexOf(requestInfo.destinationID) === -1 && deniedSelected.indexOf(requestInfo.destinationID) === -1
      })

      setDestinationRequests(tempRequests)
      NotificationToast(false, 'Successfully updated requests.')
    }

    handleConfirmClose()
    setApprovedSelected([])
    setDeniedSelected([])
  }

  const fireRequestFailure = () => {
    NotificationToast(true, 'Failed to update requests.')
  }

  const approvedCount = approvedSelected.length
  const deniedCount = deniedSelected.length
  const displayedCount = displayedRows.length
  const selectedCount = selected.length

  return (
    <>
      <Grid container direction='row'>
        <Grid item container direction='column' justify='flex-start' spacing={2} className='mx-auto w-100'>

          <Grid container item direction='row'>
            <Grid item>
              <h2>Cisco Block Requests</h2>
            </Grid>
          </Grid>

          <Grid container item direction='row'>
            <Grid item xs={12}>
              <Card raised elevation={3}>
                <CardContent>

                  <Grid container direction='row'>
                    <Grid container item direction='column'>
                      <Grid item container direction='row'>
                        <Grid item xs={12}>
                          <EnhancedTableToolbar
                            selectedCount={selectedCount}
                            onApprove={handleApprove}
                            onDeny={handleDeny}
                            approvedCount={approvedCount}
                            deniedCount={deniedCount}
                          />
                        </Grid>
                      </Grid>

                      <Grid item container direction='row'>
                        <TableContainer className={classes.requestsContainer}>
                          <Table size='medium' stickyHeader>
                            <EnhancedCiscoHead
                              classes={classes}
                              selectedCount={selectedCount}
                              approvedCount={approvedDisplayed}
                              deniedCount={deniedDisplayed}
                              displayedCount={displayedCount}
                              order={order}
                              orderBy={orderBy}
                              onSelectAllClick={handleSelectAllClick}
                              onRequestSort={handleRequestSort}
                            />

                            <TableBody>
                              {
                                displayedRows.map((destinationInfo) => {
                                  const {
                                    firstName: requestFirstName, lastName: requestLastName, emailAddress: requestEmail = '', destinationID = '',
                                    domain, comment, destinationListName = '', unitDisplay = ''
                                  } = destinationInfo

                                  const isSelected = selected.indexOf(destinationID) !== -1
                                  const isApproved = approvedSelected.indexOf(destinationID) !== -1
                                  const isDenied = deniedSelected.indexOf(destinationID) !== -1
                                  const isDisabled = !destinationInfo || !destinationInfo.destinationListID

                                  return (
                                    <React.Fragment key={`table-row-${destinationID}`}>
                                      <LightTooltip
                                        arrow
                                        title={
                                          <Grid container direction='row'>
                                            {
                                              unitDisplay
                                                ? (
                                                  <Grid container item direction='column'>
                                                    <Grid item>
                                                      <Typography variant='button'>No List found for:</Typography>
                                                    </Grid>

                                                    <Grid item>
                                                      <Typography variant='button'>{unitDisplay}</Typography>
                                                    </Grid>
                                                  </Grid>
                                                ) : (
                                                  <Grid item>
                                                    <Typography variant='button'>Request's unit has no setting mapped.</Typography>
                                                  </Grid>
                                                )
                                            }
                                          </Grid>
                                        }
                                        placement='left-end'
                                        disableFocusListener={Boolean(destinationInfo && destinationInfo.destinationListID)}
                                        disableHoverListener={Boolean(destinationInfo && destinationInfo.destinationListID)}
                                        disableTouchListener={Boolean(destinationInfo && destinationInfo.destinationListID)}
                                      >
                                        <TableRow
                                          hover
                                          onClick={() => handleRowClick(isDisabled, destinationID, isApproved, isDenied)}
                                          selected={isSelected || isApproved || isDenied || (!destinationInfo || !destinationInfo.destinationListID)}
                                          classes={{
                                            selected: clsx(classes.rowSelected, {
                                              [classes.approveSelected]: isApproved,
                                              [classes.denySelected]: isDenied,
                                              [classes.disabledSelected]: !destinationInfo || !destinationInfo.destinationListID
                                            })
                                          }}
                                        >
                                          <TableCell padding='checkbox'>
                                            <Checkbox
                                              checked={isSelected || isApproved || isDenied}
                                              disabled={!destinationInfo || !destinationInfo.destinationListID}
                                              inputProps={{ 'aria-labelledby': `checkbox-${destinationID}` }}
                                              className={clsx(classes.defaultCheckbox, {
                                                [classes.approvedCheckbox]: isApproved,
                                                [classes.deniedCheckbox]: isDenied
                                              })}
                                            />
                                          </TableCell>

                                          <TableCell align='center'>
                                            <Typography variant='subtitle2'>{requestFirstName} {requestLastName}</Typography>
                                          </TableCell>

                                          <TableCell align='center'>
                                            <Tooltip arrow title={requestEmail}>
                                              <Typography variant='subtitle2'>{truncateText(requestEmail, 12)}</Typography>
                                            </Tooltip>
                                          </TableCell>

                                          <TableCell align='center'>
                                            <Tooltip arrow title={domain}>
                                              <Link component='button' variant='subtitle2'>{truncateText(domain, 12)}</Link>
                                            </Tooltip>
                                          </TableCell>

                                          <TableCell align='center'>
                                            <Tooltip arrow title={comment}>
                                              <Typography variant='subtitle2'>{truncateText(comment, 12)}</Typography>
                                            </Tooltip>
                                          </TableCell>

                                          <TableCell align='center'>
                                            <Tooltip arrow title={unitDisplay}>
                                              <Typography variant='subtitle2'>{destinationListName}</Typography>
                                            </Tooltip>
                                          </TableCell>

                                          <TableCell align='center'>
                                            {
                                              isDisabled
                                                ? (<Typography variant='overline' className={classes.disabledText}>DISABLED</Typography>)
                                                : isApproved
                                                  ? (<Typography variant='overline' className={classes.approvedText}>APPROVED</Typography>)
                                                  : isDenied
                                                    ? (<Typography variant='overline' className={classes.deniedText}>DENIED</Typography>)
                                                    : (<Typography variant='overline' className={classes.pendingText}>PENDING</Typography>)
                                            }
                                          </TableCell>
                                        </TableRow>
                                      </LightTooltip>
                                    </React.Fragment>
                                  )
                                })
                              }
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>

                      <Grid item container direction='row' justify='space-between' alignItems='center'>
                        <Grid item>
                          <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component='div'
                            count={destinationRequests.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={(e, newPage) => setPage(newPage)}
                            onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                          />
                        </Grid>

                        <Grid item>
                          <Button
                            variant='contained'
                            className={classes.saveBtn}
                            onClick={handleSave}
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                </CardContent>
              </Card>
            </Grid>
          </Grid>

        </Grid>
      </Grid>

      <Dialog open={confirmShow} onClose={handleConfirmClose} maxWidth={false}>
        <DialogTitle disableTypography>
          <Grid container direction='row' justify='space-between' spacing={1} className={classes.fixSpacing}>
            <Grid item>
              <Typography variant='button'>Confirm Domains</Typography>
            </Grid>

            <Grid item>
              <IconButton onClick={handleConfirmClose}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container direction='row'>
            <Grid item xs>
              <List>
                {
                  approvedDomains.reduce((arr, approvedInfo = {}, approvedIndex) => {
                    const { destinationID: approvedDestinationID, hasSubdomain = false, destination: approvedDestination, domain: approvedDomain = '' } = approvedInfo

                    if (Boolean(approvedDestinationID) && hasSubdomain) {
                      const isChecked = Boolean(subdomains.indexOf(approvedDestinationID) !== -1)

                      arr.push(
                        <React.Fragment key={`approved-request-${approvedDestinationID}`}>
                          {/* <Divider /> */}

                          <ListItem>
                            <Grid container direction='row' spacing={3} className={classes.fixSpacing} justify='space-between' alignItems='center'>
                              <Grid item className='pl-0 py-0'>
                                <Typography variant='overline'>{isChecked ? approvedDomain : approvedDestination}</Typography>
                              </Grid>

                              <Grid item className='pr-0 py-0'>
                                <Grid container direction='row' alignItems='center'>
                                  <Grid item>
                                    <Typography variant='caption'>Subdomain</Typography>
                                  </Grid>

                                  <Grid item>
                                    <StyledSwitch
                                      checked={isChecked}
                                      onChange={(e) => handleDomainChange(e, approvedDestinationID)}
                                    />
                                  </Grid>

                                  <Grid item>
                                    <Typography variant='caption'>Domain</Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </ListItem>

                          {/* {
                            (approvedIndex + 1) !== approvedDomains.length
                              ? (
                                <Divider variant='fullWidth' />
                              ) : (<></>)
                          } */}
                        </React.Fragment>
                      )
                    }

                    return arr
                  }, [])
                }
              </List>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant='contained' className={classes.greenBtn} onClick={handleConfirm}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default CiscoRequests
